import { useScrollReveal } from '@/hooks/useScrollReveal';

const audienceTracks = [
  {
    title: 'Individual Radiologists',
    detail: 'Move from dictation to decision support without leaving your workflow.',
  },
  {
    title: 'Radiology Groups',
    detail: 'Keep report quality consistent while giving every reader fast access to insight.',
  },
  {
    title: 'Departments',
    detail: 'Deploy one connected platform for reporting, guidance, and operational consistency.',
  },
];

export function PainNarrative() {
  const { ref, isVisible } = useScrollReveal();

  return (
    <section className="h-screen flex items-center px-4 scroll-snap-align-start">
      <div
        ref={ref}
        className={`max-w-6xl mx-auto w-full ${isVisible ? 'reveal-visible' : 'reveal-hidden'}`}
      >
        <div className="mb-8 text-center">
          <p className="text-xs uppercase tracking-[0.16em] text-primary font-semibold mb-3">The Reality</p>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-light text-foreground tracking-tight">
            High volume. High complexity. Fragmented tools.
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground max-w-3xl mx-auto">
            Modern radiology needs more than a one-off AI add-on. RadiologyLive.ai connects reporting, clinical insight,
            and workflow support so radiologists can stay focused in one operating environment.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {audienceTracks.map((track) => (
            <div key={track.title} className="rounded-xl border border-border/60 bg-card/60 p-5">
              <h3 className="text-sm font-semibold text-foreground mb-2">{track.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{track.detail}</p>
            </div>
          ))}
        </div>

        <div className="mt-6 rounded-xl border border-border/60 bg-card/60 p-4">
          <div className="text-xs uppercase tracking-[0.14em] text-primary font-semibold mb-3">
            Why a connected suite beats disconnected tools
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
            <div className="rounded-lg border border-border/50 p-3">
              <div className="font-semibold text-foreground mb-1">Single-purpose add-ons</div>
              <ul className="space-y-1 text-muted-foreground">
                <li>Frequent context switching across tabs and sites</li>
                <li>Knowledge lookup detached from reporting flow</li>
                <li>Inconsistent output and review burden</li>
              </ul>
            </div>
            <div className="rounded-lg border border-primary/40 bg-primary/5 p-3">
              <div className="font-semibold text-foreground mb-1">RadiologyLive.ai</div>
              <ul className="space-y-1 text-muted-foreground">
                <li>LiveSpeak and LiveInsight in one unified workflow</li>
                <li>Structured report compilation with section-aware logic</li>
                <li>Traceable output designed for quality review and compliance</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
